import { useState } from "react";
import { Loader2, Play, Settings2, CheckCheck } from "lucide-react";
import { toast } from "sonner";

const inputCls = "bg-[#0b0811] border border-white/10 text-white rounded-lg px-2 py-1.5 text-xs focus:border-[#f7c935] outline-none";

function Toggle({ checked, onChange, label, testId }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex items-center gap-2 text-xs text-slate-300" data-testid={testId} aria-pressed={checked}>
      <span className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${checked ? "bg-[#22c55e]" : "bg-white/15"}`}>
        <span className={`inline-block h-4 w-4 rounded-full bg-white transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
      </span>
      {label}
    </button>
  );
}

export function AutoSettingsBar({ api, societeId, settings, toReview, onChanged, onRun, onApproveAll }) {
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [approving, setApproving] = useState(false);
  const [days, setDays] = useState(settings.bank_alert_days);

  const save = async (patch) => {
    setSaving(true);
    try {
      await api.put("/accounting/auto/settings", { societe_id: societeId, ...patch });
      toast.success("Paramètres enregistrés");
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/accounting/auto/run", null, { params: { societe_id: societeId } });
      const s = r.data.stats || {};
      toast.success(`Génération terminée : ${s.validated || 0} validée(s), ${s.to_review || 0} à vérifier, ${s.matched || 0} pointée(s)`);
      onRun(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setRunning(false);
  };

  const approveAll = async () => {
    if (!window.confirm(`Valider les ${toReview} écriture(s) en attente telles quelles ?`)) return;
    setApproving(true);
    try {
      const r = await api.post("/accounting/auto/review/approve-all", null, { params: { societe_id: societeId } });
      toast.success(`${r.data.approved} écriture(s) validée(s)`);
      onApproveAll();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setApproving(false);
  };

  return (
    <div className="bg-[#0b0811] border border-white/5 rounded-xl p-4 flex flex-wrap items-center gap-4" data-testid="auto-settings-bar">
      <Settings2 className="w-4 h-4 text-slate-500" />
      <Toggle checked={settings.auto_enabled} onChange={(v) => save({ auto_enabled: v })} label="Moteur automatique (toutes les heures)" testId="auto-enabled-toggle" />
      <Toggle checked={settings.auto_validate} onChange={(v) => save({ auto_validate: v })} label="Validation auto si confiance haute" testId="auto-validate-toggle" />
      <label className="flex items-center gap-2 text-xs text-slate-300">
        Alerte sans justificatif après
        <input type="number" min="1" max="180" value={days} onChange={(e) => setDays(e.target.value)} onBlur={() => Number(days) !== settings.bank_alert_days && save({ bank_alert_days: Number(days) })} className={`${inputCls} w-16 text-center`} data-testid="bank-alert-days-input" />
        jours
      </label>
      {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-500" />}
      <div className="flex-1" />
      {toReview > 0 && (
        <button onClick={approveAll} disabled={approving} className="text-emerald-400 hover:text-emerald-300 text-xs font-bold px-3 py-2 rounded-lg hover:bg-white/5 flex items-center gap-1.5 disabled:opacity-50" data-testid="approve-all-btn">
          {approving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCheck className="w-3.5 h-3.5" />} Tout valider ({toReview})
        </button>
      )}
      <button onClick={run} disabled={running} className="bg-[#f7c935] hover:bg-[#e6b825] text-[#0b0811] font-bold text-xs px-4 py-2 rounded-lg flex items-center gap-1.5 transition-colors disabled:opacity-50" data-testid="auto-run-btn">
        {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Lancer la génération
      </button>
    </div>
  );
}
